/* global APPLICATION_CONFIG */
import c from "./consts";
import { format } from "date-fns";
import axios from "axios";


// =====================
// Helpers
// =====================
const LS_KEY = "selectedLocation";
const DEFAULT_LOCATION_ID = 11;
// Модель
const SELECTED_MODEL_LS_KEY = "selectedModel";
// Локация морского почасового 
const SELECTED_WAVE_LOCATION_LS_KEY = "selectedWaveLocation";

async function requestGeolocationAfterLoad(dispatch) {
  if (!navigator.geolocation) {
    return null;
  }

  if (navigator.permissions?.query) {
    try {
      const perm = await navigator.permissions.query({
        name: "geolocation",
      });

      if (perm.state === "denied") {
        return null;
      }
    } catch (e) {
      // игнорируем ошибку Permissions API
    }
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const coords = {
              lat: position.coords.latitude,
              lon: position.coords.longitude,
              accuracy: position.coords.accuracy,
            };

            const nearestLocation = await dispatch(c.GET_NEAREST_LOCATION, coords);

            resolve(nearestLocation);
          } catch (e) {
            resolve(null);
          }
        },
        () => resolve(null),
        {
          enableHighAccuracy: true,
          timeout: 10000,
        }
    );
  });
}

function safeParse(json) {
  try { return JSON.parse(json); } catch (e) { return null; }
}

function saveSelectedLocation(location) {
  if (!location) return;

  localStorage.setItem(LS_KEY, JSON.stringify(location));
}

function getSelectedLocation(locations = []) {
  const saved = safeParse(localStorage.getItem(LS_KEY));
  const savedId = Number(saved?.id);
  const locationId = Number.isFinite(savedId) && savedId > 0
    ? savedId
    : DEFAULT_LOCATION_ID;

  const actualLocation = locations.find(
    (location) => Number(location.id) === locationId
  );

  if (actualLocation) {
    saveSelectedLocation(actualLocation);
    return actualLocation;
  }

  if (saved && Number.isFinite(savedId) && savedId > 0) {
    return saved;
  }

  return { id: DEFAULT_LOCATION_ID, name: "Локация по умолчанию" };
}

function getSavedWaveLocationId() {
  try {
    const savedValue = localStorage.getItem(
      SELECTED_WAVE_LOCATION_LS_KEY
    );

    if (!savedValue) {
      return null;
    }

    const parsedValue = safeParse(savedValue);

    if (
      parsedValue &&
      typeof parsedValue === "object" &&
      parsedValue.id !== undefined
    ) {
      return parsedValue.id;
    }

    return parsedValue ?? savedValue;
  } catch (error) {
    console.error(
      "Не удалось прочитать выбранную волновую локацию",
      error
    );

    return null;
  }
}

function saveWaveLocationId(locationId) {
  try {
    localStorage.setItem(
      SELECTED_WAVE_LOCATION_LS_KEY,
      JSON.stringify(locationId)
    );
  } catch (error) {
    console.error(
      "Не удалось сохранить выбранную волновую локацию",
      error
    );
  }
}

// ВСЕ запросы "по выбранной локации" (строго по очереди + лог диспатча)
async function fetchAllForecasts(dispatch, locationId, selectedModel) {
  await dispatch(c.GET_CURRENT_FORECAST, { locationId, selectedModel });
  await dispatch(c.GET_HOURLY_FORECAST, { locationId, selectedModel });
  await dispatch(c.GET_MODEL_FORECAST, { locationId, selectedModel });
  await dispatch(c.GET_SYNOPTIC_FORECAST, locationId);
  await dispatch(c.GET_AI_FORECAST,locationId);
  await dispatch(c.GET_MIRINE_FORECAST);
  await dispatch(c.GET_WARNING);
  await dispatch(c.GET_AMK_DATA);
  await dispatch(c.GET_GRAPH_DATA, { locationId, selectedModel });
}

export default {
  async [c.INIT_STORE]({ dispatch, commit, state }) {
    try {
      await dispatch(c.GET_LOCATIONS);
      await dispatch(c.GET_WAVE_LOCATIONS);
      if (state[c.SELECTED_WAVE_LOCATION]) {
        await dispatch(
          c.GET_WAVE_FORECAST,
          {
            locationId: state[c.SELECTED_WAVE_LOCATION].id
          }
        );
      }
      let selectedLocation = getSelectedLocation(state[c.LOCATIONS]);
      commit(c.SELECTED_LOCATION, selectedLocation);

      await dispatch(c.GET_AVAILABLE_MODELS, selectedLocation.id);
      await fetchAllForecasts(dispatch, selectedLocation.id, state[c.SELECTED_MODEL]);

      const geolocation = await requestGeolocationAfterLoad(dispatch);
      if (geolocation) {
        const locationChanged =
          Number(geolocation.id) !== Number(selectedLocation.id);

        commit(c.SELECTED_LOCATION, geolocation);
        saveSelectedLocation(geolocation);

        if (locationChanged) {
          selectedLocation = geolocation;
          await dispatch(c.GET_AVAILABLE_MODELS, selectedLocation.id);
          await fetchAllForecasts(
            dispatch,
            selectedLocation.id,
            state[c.SELECTED_MODEL]
          );
        }
      }

      await dispatch(c.GET_RADAR_SADG);
      await dispatch(c.GET_RADAR_DALN);
      await dispatch(c.GET_TYPHOON_GEOJSON);

    } catch (error) {
      console.error("Ошибка при инициализации", error);
    }
  },

  // ===================== REFRESHER ===================== //
  async [c.REFRESH_ALL_FORECASTS]({ dispatch, state }) {
    try {
      const savedLocation = getSelectedLocation(state?.[c.LOCATIONS]);
      let locationId = state?.[c.SELECTED_LOCATION]?.id || savedLocation.id;
      let selectedModel = state?.[c.SELECTED_MODEL];

      await fetchAllForecasts(dispatch, locationId, selectedModel);
    } catch (error) {
      console.error("Ошибка при обновлении всех прогнозов", error);
    }
  },

  async [c.GET_NEAREST_LOCATION]({ dispatch, commit, state }, coords) {
    try {
      const resp = await axios.get(`${APPLICATION_CONFIG.api.baseURL}/get-nearest-location`, {
        params: { longitude: coords.lon, latitude: coords.lat },
      });

      return resp.data;
    } catch (error) {
      console.error("Ошибка при получении локации по координатам", error);
      return null;
    }
  },

  // ===================== AVAILABLE_MODELS ===================== //
  async [c.GET_AVAILABLE_MODELS]({ commit }, locationId) {
    try {
      const response = await axios.get(
        `${APPLICATION_CONFIG.api.baseURL}/get-available-models`,
        {
          params: { locationId },
        }
      );

      const availableModels = Array.isArray(response.data)
        ? response.data
        : [];

      commit(c.AVAILABLE_MODELS, availableModels);

      const savedModel = localStorage.getItem(SELECTED_MODEL_LS_KEY);

      let selectedModel = null;

      // Если сохранённая пользователем модель доступна для этой локации —
      // восстанавливаем её.
      if (savedModel && availableModels.includes(savedModel)) {
        selectedModel = savedModel;
      } else if (availableModels.length) {
        // Если сохранённой модели нет в списке —
        // выбираем первую доступную.
        selectedModel = availableModels[0];
      }

      commit(c.SELECTED_MODEL, selectedModel);

      // Сохраняем фактически выбранную модель.
      // Это также обновит localStorage, если старая модель больше недоступна.
      if (selectedModel) {
        localStorage.setItem(SELECTED_MODEL_LS_KEY, selectedModel);
      }
    } catch (error) {
      console.error("Ошибка при получении моделей", error);

      commit(c.AVAILABLE_MODELS, []);
      commit(c.SELECTED_MODEL, null);
    }
  },

  // ===================== WAVE ===================== //
  async [c.GET_WAVE_FORECAST]({ commit }, payload) {
    try {
      const { locationId } = payload;

      const response = await axios.get(
        `${APPLICATION_CONFIG.api.baseURL}/get-wave-gfs-forecasts`,
        {
          params: {
            locationId
          }
        }
      );

      commit(c.WAVE_FORECAST, response.data || []);
    } catch (error) {
      console.error("Ошибка при получении Wave", error);
      commit(c.WAVE_FORECAST, []);
    }
  },

  // ===================== TYPHOON GEOJSON ===================== //
  async [c.GET_TYPHOON_GEOJSON]({ commit }) {
    try {
      const response = await axios.get(
        `${APPLICATION_CONFIG.api.baseURL}/get-typhoon-geojson`,
        {
          params: {
            ts: Date.now(),
          },
        }
      );
      console.log(response.data);
      commit(c.TYPHOON_GEOJSON, response.data);

      return response.data;
    } catch (error) {
      console.error(
        "Ошибка при получении GeoJSON тайфуна",
        error
      );
      
      commit(c.TYPHOON_GEOJSON, []);

      return [];
    }
  },

  // ===================== CURRENT ===================== //
  async [c.GET_CURRENT_FORECAST]({ commit }, payload) {
    try {
      const { locationId, selectedModel } = payload;

      const response = await axios.get(`${APPLICATION_CONFIG.api.baseURL}/get-current-forecast`, {
        params: { locationId, model: selectedModel },
      });

      commit(c.CURRENT_FORECAST, response.data);
    } catch (error) {
      console.error("Ошибка при получении текущей погоды", error);
    }
  },

  // ===================== LOCATIONS ===================== //
  async [c.GET_LOCATIONS]({ commit }) {
    try {
      const response = await axios.get(`${APPLICATION_CONFIG.api.baseURL}/get-locations`, {
        params: { regionId: 3, locationType: "locality" },
      });

      commit(c.LOCATIONS, response.data);
    } catch (error) {
      console.error("Ошибка при получении локаций", error);
    }
  },

  // ===================== WAVE LOCATIONS ===================== //
  async [c.GET_WAVE_LOCATIONS]({ commit }) {
    try {
      const response = await axios.get(
        `${APPLICATION_CONFIG.api.baseURL}/get-locations`,
        {
          params: {
            regionId: 3,
            locationType: "cove",
          },
        }
      );

      const waveLocations = Array.isArray(response.data)
        ? response.data
        : [];

      commit(c.WAVE_LOCATIONS, waveLocations);

      if (!waveLocations.length) {
        commit(c.SELECTED_WAVE_LOCATION, null);
        commit(c.WAVE_FORECAST, []);
        return null;
      }

      const savedLocationId = getSavedWaveLocationId();

      /*
      * Ищем сохранённую локацию в актуальном списке.
      * String используется на случай, если id в одном месте число,
      * а в другом строка.
      */
      const savedLocation = waveLocations.find(
        (location) =>
          String(location.id) === String(savedLocationId)
      );

      /*
      * Если сохранённой локации больше нет,
      * выбираем первую доступную.
      */
      const selectedLocation =
        savedLocation || waveLocations[0];

      commit(
        c.SELECTED_WAVE_LOCATION,
        selectedLocation
      );

      saveWaveLocationId(selectedLocation.id);

      return selectedLocation;
    } catch (error) {
      console.error(
        "Ошибка при получении волновых локаций",
        error
      );

      commit(c.WAVE_LOCATIONS, []);
      commit(c.SELECTED_WAVE_LOCATION, null);
      commit(c.WAVE_FORECAST, []);

      return null;
    }
  },

  // ===================== AMK ===================== //
  async [c.GET_AMK_DATA]({ commit }) {
    try {
      const response = await axios.get(`${APPLICATION_CONFIG.api.baseURL}/get-amk-data`);

      commit(c.AMK_DATA, response.data);
    } catch (error) {
      console.error("Ошибка при получении даты с АМК: ", error);
    }
  },

  // ===================== AMK12HOURS ===================== //
  async [c.GET_GRAPH_DATA] ({ commit }, responseParams) {
    try {
      const { locationId, selectedModel } = responseParams;

      const response = await axios.get(`${APPLICATION_CONFIG.api.baseURL}/get-graph-data`, {
        params: { locationId, model: selectedModel },
      });

      commit(c.GRAPH_DATA, response.data);
    } catch (error) {
      console.error("Ошибка при получении данные АМК 12 часов: ", error);
    }
  },

  // ===================== HOURLY ===================== //
  async [c.GET_HOURLY_FORECAST]({ commit }, payload) {
    try {
      const { locationId, selectedModel } = payload;

      const response = await axios.get(`${APPLICATION_CONFIG.api.baseURL}/get-hourly-forecasts`, {
        params: { locationId, model: selectedModel},
      });

      commit(c.HOURLY_FORECAST, response.data);
    } catch (error) {
      console.error("Ошибка при получении часовых прогнозов", error);
    }
  },

  // ===================== MODEL ===================== //
  async [c.GET_MODEL_FORECAST]({ commit }, payload) {
    try {
      const { locationId, selectedModel } = payload;
        let response = await axios.get(`${APPLICATION_CONFIG.api.baseURL}/get-12-hours-forecasts`, {
          params: { locationId, model: selectedModel },
        });

      commit(c.MODEL_FORECAST, response.data);
    } catch (error) {
      console.error("Ошибка при получении модельных прогнозов", error);
    }
  },

  // ===================== SYNOPTIC ===================== //
  async [c.GET_SYNOPTIC_FORECAST]({ commit }, locationId) {
    try {

      const response = await axios.get(`${APPLICATION_CONFIG.api.baseURL}/get-12-hours-forecasts`, {
        params: { locationId, model: "forecasts-40" },
      });

      commit(c.SYNOPTIC_FORECAST, response.data);
    } catch (error) {
      console.error("Ошибка при получении синоптических прогнозов", error);
    }
  },

  // ===================== AI ===================== //
  async [c.GET_AI_FORECAST]({ commit }, locationId) {
    try {

      const response = await axios.get(`${APPLICATION_CONFIG.api.baseURL}/get-ai-forecast`, {
        params: { locationId },
      });

      commit(c.AI_FORECAST, response.data);
    } catch (error) {
      console.error("Ошибка при получении ИИ прогноза", error);
    }
  },

  // ===================== MIRINE ===================== //
  async [c.GET_MIRINE_FORECAST]({ commit }) {
    try {
      const resp = await axios.get(`${APPLICATION_CONFIG.api.baseURL}/get-mirine-forecasts`);

      commit(c.MIRINE_FORECAST, resp.data);
    } catch (error) {
      console.error("Ошибка при получении морского прогноза", error);
    }
  },

  // ===================== SEND FEEDBACK ===================== //
  async [c.SEND_FEEDBACK](_, { feedbackText, images = [] }) {
    try {
      const formData = new FormData();
      formData.append("feedbackText", feedbackText || "");

      images.forEach((image) => {
        formData.append("images", image, image.name);
      });

      const response = await axios.post(
          `${APPLICATION_CONFIG.api.baseURL}/send-feedback`,
          formData
      );
      return response.data;
    } catch (error) {
      console.error("Ошибка при отправке feedback", error);
      throw error;
    }
  },

  // ===================== SYN MAPS ===================== //
  async [c.GET_SYN_MAPS]({ commit }) {
    try {
      const response = await axios.get(`${APPLICATION_CONFIG.api.baseURL}/get-syn-maps`, {
        params: {
          ts: Date.now(),
        },
      });

      const data = response.data || {};

      const prepared = {
        updated_at: data.updated_at || null,
        run_date: data.run_date || null,
        run_hour: data.run_hour || null,
        items: Array.isArray(data.items)
          ? data.items.map((item) => ({
              forecast_hour: item.forecast_hour,
              valid_time: item.valid_time,
              url: item.url,
            }))
          : [],
      };

      commit(c.SYN_MAPS, prepared);
      return prepared;
    } catch (error) {
      console.error("Ошибка при получении прогностических карт", error);

      const emptyData = {
        updated_at: null,
        run_date: null,
        run_hour: null,
        items: [],
      };

      commit(c.SYN_MAPS, emptyData);
      return emptyData;
    }
  },

  // Получает список опубликованных моделей интерактивной карты.
  async [c.GET_PREDICTIVE_MAP_MODELS]() {
    const response = await axios.get(
      `${APPLICATION_CONFIG.api.baseURL}/get-predictive-map-models`,
      { params: { ts: Date.now() } }
    );

    return Array.isArray(response.data) ? response.data : [];
  },

  // Получает manifest интерактивных прогностических карт.
  async [c.GET_PREDICTIVE_MAPS_NEW](_, model = "gfs") {
    const response = await axios.get(
      `${APPLICATION_CONFIG.api.baseURL}/get-predictive-maps-new`,
      {
        params: { model, ts: Date.now() },
      }
    );

    const data = response.data || {};

    return {
      schema_version: data.schema_version || 1,
      updated_at: data.updated_at || null,
      model: data.model || null,
      run_date: data.run_date || null,
      run_hour: data.run_hour || null,
      bounds: data.bounds || null,
      items: Array.isArray(data.items)
        ? data.items.map((item) => ({
            forecast_hour: Number(item.forecast_hour) || 0,
            valid_time: item.valid_time || null,
            data_file: item.data_file,
          }))
        : [],
    };
  },

  // Загружает один JSON-кадр только в момент его показа.
  async [c.GET_PREDICTIVE_MAP_FRAME](_, dataFile) {
    const response = await axios.get(
      `${APPLICATION_CONFIG.api.baseURL}/get-predictive-map-frame`,
      {
        params: { file: dataFile },
      }
    );

    return response.data;
  },

  // ===================== WARNING ===================== //
  async [c.GET_WARNING]({ commit }) {
    try {

      const response = await axios.get(`${APPLICATION_CONFIG.api.baseURL}/get-warning`, {
        params: { regionId: 3 },
      });

      commit(c.WARNING, response.data);
    } catch (error) {
      console.error("Ошибка при получении предупреждений", error);
    }
  },

  // ===================== METEORADAR SADG ===================== //
  async [c.GET_RADAR_SADG]({commit}) {
    try {

      const response = await axios.get(`${APPLICATION_CONFIG.api.baseURL}/get-radar-sadg`);

      commit(c.RADAR_SADG, response.data);
    } catch (error) {
      console.error("Ошибка при получении данных радара Садгород", error);
    }
  },

  // ===================== METEORADAR DALN ===================== //
  async [c.GET_RADAR_DALN]({commit}) {
    try {

      const response = await axios.get(`${APPLICATION_CONFIG.api.baseURL}/get-radar-daln`);

      commit(c.RADAR_DALN, response.data);
    } catch (error) {
      console.error("Ошибка при получении данных радара Садгород", error);
    }
  },

};
